/* Domovoi · who's signed in, cached for instant nav rendering.

   The marketing pages deliberately don't load the Supabase SDK — the nav
   would pay ~40KB for a name. Instead the pages that DO have the SDK
   (sign-in, join, Catered) write a small blob here, and the nav reads it
   with no network at all. The JWT is the fallback: it always carries the
   email and user id, so a first visit still renders something true. */
import { API_URL, PUBLISHABLE_KEY, AUTH_STORAGE_KEY } from './config.js';
import { storage } from './storage.js';
import { stored, session, live, fromToken } from './session.js';

(function () {
  var KEY   = 'domovoi.identity.v1';
  var TOKEN = AUTH_STORAGE_KEY;

  function initialOf(name, email) {
    var src = (name || email || '?').trim();
    return src ? src[0].toUpperCase() : '?';
  }

  function read() {
    try {
      var v = JSON.parse(storage.get(KEY) || 'null');
      return (v && typeof v === 'object') ? v : null;
    } catch (e) { return null; }
  }

  /* What the nav actually renders: cache first, JWT to fill the gaps. */
  function current() {
    var s = session();
    if (!s) return null;
    var jwt = fromToken(s);
    var c = read() || {};
    // A cache belonging to someone else (shared browser, account switch) is ignored.
    if (c.id && jwt.id && c.id !== jwt.id) c = {};
    var name  = c.name || null;
    var email = c.email || jwt.email || null;
    return {
      id: jwt.id || c.id || null,
      name: name,
      email: email,
      initial: initialOf(name, email),
      partner: c.partner || null,
      partnerInitial: c.partner ? initialOf(c.partner, null) : null,
      partnerKnown: !!c.checkedAt,
    };
  }

  function save(patch) {
    var s = session();
    if (!s) return;
    var jwt = fromToken(s);
    var next = Object.assign({}, read() || {}, patch, { id: jwt.id, updatedAt: Date.now() });
    if (!next.email) next.email = jwt.email || null;
    storage.set(KEY, JSON.stringify(next));
  }

  function clear() { storage.remove(KEY); }

  /* ---- Filling the cache without the SDK ----------------------------------
     The nav holds a valid JWT, and PostgREST applies RLS with it. The profiles
     policy is "you, or whoever you live with", so one request returns exactly
     one or two rows — which is all the menu needs. ~10 lines instead of 40KB.

     Without this the cache only ever filled on the pages that load the SDK,
     so anyone already signed in saw an email and no partner — and the menu
     read that as "solo" and offered to invite a partner they already had. */
  var API = API_URL;
  var PUBLISHABLE = PUBLISHABLE_KEY;
  var STALE_MS = 6 * 60 * 60 * 1000;

  function refresh() {
    var s = live();
    if (!s) return Promise.resolve(null);   // try again on the next page that has a fresh token
    var jwt = fromToken(s);
    return fetch(API + '/rest/v1/profiles?select=id,first_name', {
      headers: { apikey: PUBLISHABLE, Authorization: 'Bearer ' + s.access_token }
    }).then(function (r) {
      if (!r.ok) throw new Error('profiles ' + r.status);
      return r.json();
    }).then(function (rows) {
      if (!Array.isArray(rows)) throw new Error('bad shape');
      var me = null, partner = null;
      rows.forEach(function (row) {
        if (row.id === jwt.id) me = row; else partner = row;
      });
      save({
        name: (me && me.first_name) || null,
        email: jwt.email || null,
        partner: (partner && partner.first_name) || null,
        // Marks the partner question as answered — null now means "no partner",
        // not "don't know yet".
        checkedAt: Date.now()
      });
      return current();
    }).catch(function () { return null; });
  }

  function isStale() {
    var c = read();
    return !c || !c.checkedAt || (Date.now() - c.checkedAt) > STALE_MS;
  }

  window.DVIdentity = {
    KEY: KEY, TOKEN: TOKEN, session: session, live: live, read: read, current: current,
    save: save, clear: clear, refresh: refresh, isStale: isStale
  };
})();

export var DVIdentity = window.DVIdentity;
